/**
 * 0zzz-fe: fired by the API client when a write is refused because the
 * client's organization context is ambiguous or stale —
 * 403 ORG_HEADER_INVALID (the stored org is no longer a valid membership) or
 * 403 ORG_SELECTION_REQUIRED (no org selected, 2+ memberships, under
 * ORG_SELECTION_ENFORCEMENT=enforce).
 *
 * The event is CANCELABLE (mirrors the tosEvents.ts pattern, but with an
 * opt-out instead of a bare notify): OrgPickerSheet listens and calls
 * preventDefault() to tell the client it will handle re-selection in place.
 * If `!window.dispatchEvent(event)` (i.e. something called preventDefault),
 * the client must NOT clear localStorage or hard-redirect — see client.ts's
 * dispatch site for why clearing would defeat the fail-closed behavior
 * ORG_HEADER_INVALID exists to provide. If nothing handles it (no picker
 * mounted), the client falls back to its pre-existing clear+redirect
 * behavior unchanged.
 */
export const ORG_PICKER_REQUIRED_EVENT = 'orgPicker:required';

export interface OrgPickerRequiredEventDetail {
  /** 'stale' = ORG_HEADER_INVALID (the stored org itself is invalid).
   *  'selection' = ORG_SELECTION_REQUIRED (no org was selected at all). */
  reason: 'stale' | 'selection';
  /** The org id that was stored in localStorage at the time of the failed
   * request. Only meaningful for reason: 'stale' — excluded from the
   * picker's list since re-selecting it would just fail again. Undefined
   * for reason: 'selection' (no header was sent, so there was no stale org
   * to identify). */
  staleOrgId?: string;
}

/** 0zzz-uhv: a notice that offers the org picker ("Select a hierarchy") after a safeword alert went to a different org than the user had selected. */
export interface OrgPickerNotice {
  text: string;
  reason: 'selection' | 'stale';
  staleOrgId?: string;
}

/** Opens the app-wide OrgPickerSheet (it listens for ORG_PICKER_REQUIRED_EVENT). */
export function openOrgPicker(notice?: OrgPickerNotice | null): void {
  const detail: OrgPickerRequiredEventDetail =
    notice?.reason === 'stale' ? { reason: 'stale', staleOrgId: notice.staleOrgId } : { reason: 'selection' };
  window.dispatchEvent(new CustomEvent(ORG_PICKER_REQUIRED_EVENT, { cancelable: true, detail }));
}
